import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { quickLeadSchema, type QuickLead } from "@shared/schema";
import { useMutation } from "@tanstack/react-query";
import { useState, useRef, useEffect, useId } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { trackFormSubmit } from "@/lib/tracking";
import { Link } from "wouter";
import { Send, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

interface QuickFormProps {
  defaultService?: "urgente" | "estandar";
  source: QuickLead["leadSource"];
  className?: string;
}

export default function QuickForm({ defaultService = "urgente", source, className = "" }: QuickFormProps) {
  const uid = useId();
  const { toast } = useToast();
  const [honeypot, setHoneypot] = useState("");
  const formLoadTime = useRef(Date.now());

  useEffect(() => {
    formLoadTime.current = Date.now();
  }, []);

  const form = useForm<QuickLead>({
    resolver: zodResolver(quickLeadSchema),
    defaultValues: {
      contactPerson: "",
      phone: "",
      email: "",
      serviceType: defaultService,
      origin: "",
      destination: "",
      leadSource: source,
      rgpdConsent: undefined as unknown as true,
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: QuickLead) => {
      const res = await apiRequest("POST", "/api/leads/quick", { ...data, _hp: honeypot, _ts: formLoadTime.current });
      return res.json();
    },
    onSuccess: () => {
      trackFormSubmit(source, form.getValues("serviceType"));
      toast({
        title: "Solicitud enviada",
        description: "Nos pondremos en contacto contigo lo antes posible.",
      });
      form.reset();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Hubo un problema al enviar tu solicitud. Inténtalo de nuevo.",
        variant: "destructive",
      });
    },
  });

  return (
    <form
      onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
      className={`space-y-4 ${className}`}
      data-testid="quick-form"
    >
      <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
        <input
          type="text"
          name="website"
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-name`}>Nombre *</Label>
          <Input
            id={`${uid}-name`}
            placeholder="Tu nombre"
            {...form.register("contactPerson")}
            data-testid="input-quick-name"
          />
          {form.formState.errors.contactPerson && (
            <p className="text-xs text-destructive">{form.formState.errors.contactPerson.message}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-phone`}>Teléfono *</Label>
          <Input
            id={`${uid}-phone`}
            type="tel"
            placeholder="600 000 000"
            {...form.register("phone")}
            data-testid="input-quick-phone"
          />
          {form.formState.errors.phone && (
            <p className="text-xs text-destructive">{form.formState.errors.phone.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${uid}-email`}>Email *</Label>
        <Input
          id={`${uid}-email`}
          type="email"
          placeholder="tu@email.com"
          {...form.register("email")}
          data-testid="input-quick-email"
        />
        {form.formState.errors.email && (
          <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-origin`}>Origen</Label>
          <Input
            id={`${uid}-origin`}
            placeholder="Ciudad / Provincia"
            {...form.register("origin")}
            data-testid="input-quick-origin"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-destination`}>Destino</Label>
          <Input
            id={`${uid}-destination`}
            placeholder="Ciudad / Provincia"
            {...form.register("destination")}
            data-testid="input-quick-destination"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Tipo de servicio</Label>
        <Select
          value={form.watch("serviceType")}
          onValueChange={(v) => form.setValue("serviceType", v as "urgente" | "estandar")}
        >
          <SelectTrigger data-testid="select-quick-service" aria-label="Tipo de servicio">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="urgente">Urgente</SelectItem>
            <SelectItem value="estandar">Estándar</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-start gap-2">
        <Checkbox
          id={`${uid}-rgpd`}
          checked={form.watch("rgpdConsent") === true}
          onCheckedChange={(checked) => form.setValue("rgpdConsent", checked === true ? true : undefined as unknown as true)}
          data-testid="checkbox-quick-rgpd"
        />
        <Label htmlFor={`${uid}-rgpd`} className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
          Acepto la{" "}
          <Link href="/politica-privacidad">
            <span className="underline cursor-pointer">política de privacidad</span>
          </Link>{" "}
          y el tratamiento de mis datos para gestionar mi solicitud. *
        </Label>
      </div>
      {form.formState.errors.rgpdConsent && (
        <p className="text-xs text-destructive">{form.formState.errors.rgpdConsent.message}</p>
      )}

      <Button type="submit" className="w-full" disabled={mutation.isPending} data-testid="button-quick-submit">
        {mutation.isPending ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Send className="w-4 h-4 mr-2" />
        )}
        {mutation.isPending ? "Enviando..." : "Solicitar Presupuesto"}
      </Button>
    </form>
  );
}
